import { ArrowRight } from "lucide-react";

const pairs = [
  {
    before: "/img/deck-before.jpg",
    after: "/img/deck-after.jpg",
    title: "Wood Deck Restoration",
    location: "Bay County, FL",
    desc: "Pressure-washed, sanded, and stained — raw, weathered pine brought back to a rich, even brown finish.",
  },
  {
    before: "/img/house-before.jpg",
    after: "/img/house-after.jpg",
    title: "Two-Story Exterior Repaint",
    location: "Bay County, FL",
    desc: "Full exterior repaint — fresh siding color, crisp white trim, and a clean balcony to match. Same house, brand-new look.",
  },
  {
    before: "/img/cottage-before.jpg?v=3",
    after: "/img/cottage-after.jpg?v=3",
    title: "Cottage Color Change",
    location: "Bay County, FL",
    desc: "Bold teal exterior refreshed with a clean, modern light blue — same little cottage, brand-new look.",
  },
  {
    before: "/img/commercial-before.jpg",
    after: "/img/commercial-after.jpg",
    title: "Commercial Repaint",
    location: "Bay County, FL",
    desc: "Dated brown stucco refreshed with a confident, modern blue. Same entry, completely different curb appeal.",
  },
];

export default function BeforeAfter() {
  return (
    <section id="before-after" className="bg-white py-24 sm:py-32 border-y-2 border-[#334155]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#0EA5E9]">
              Before · After
            </div>
            <h2 className="mt-3 font-heading font-black uppercase tracking-tight text-[#334155] text-4xl sm:text-5xl leading-[0.95]">
              The Same Deck.<br />A World Of Difference.
            </h2>
          </div>
          <p className="max-w-md text-base text-[#4B5563]">
            Every project starts the same way — a careful prep, the right product, and the
            patience to do it right. Here's what that looks like in practice.
          </p>
        </div>

        {pairs.map((p, i) => (
          <article key={i} className={`grid lg:grid-cols-2 gap-3 sm:gap-5 items-stretch ${i > 0 ? "mt-16 pt-16 border-t-2 border-[#E5E7EB]" : ""}`}>
            <Frame src={p.before} label="Before" tone="before" />
            <Frame src={p.after} label="After" tone="after" />
            <div className="lg:col-span-2 mt-2 pt-6 border-t-2 border-[#E5E7EB] flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold uppercase tracking-tight text-[#334155] text-2xl sm:text-3xl">
                  {p.title}
                </h3>
                <div className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563]">
                  {p.location}
                </div>
              </div>
              <p className="max-w-md text-sm sm:text-base text-[#4B5563] leading-relaxed">
                {p.desc}
              </p>
            </div>
          </article>
        ))}

        <div className="mt-14 text-center">
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="before-after-cta"
            className="group inline-flex items-center gap-3 bg-[#0EA5E9] hover:bg-[#0284C7] transition-colors text-white px-7 py-4 font-heading font-bold uppercase tracking-wider"
          >
            Want results like this?
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}

function Frame({ src, label, tone }) {
  const accent = tone === "after" ? "bg-[#0EA5E9]" : "bg-[#334155]";
  return (
    <figure className="relative group overflow-hidden">
      <div className="aspect-square overflow-hidden bg-[#334155]">
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <figcaption className={`absolute top-4 left-4 ${accent} text-white px-4 py-2 font-heading font-black uppercase tracking-[0.2em] text-sm`}>
        {label}
      </figcaption>
    </figure>
  );
}
